import { useState, useEffect } from "react";
import { Save, Loader2, Facebook, Instagram, Youtube, Music2, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

const socialIcons = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  tiktok: Music2,
  x: Twitter,
};

const socialColors = {
  facebook: "bg-[#1877F2] text-white",
  instagram: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white",
  youtube: "bg-[#FF0000] text-white",
  tiktok: "bg-black text-white",
  x: "bg-black text-white",
};

export default function AdminRedesSociales() {
  const [networks, setNetworks] = useState([
    { id: "facebook", name: "Facebook", url: "https://facebook.com/cholutecanet", followers: 0 },
    { id: "instagram", name: "Instagram", url: "https://instagram.com/cholutecanet", followers: 0 },
    { id: "tiktok", name: "TikTok", url: "https://tiktok.com/@cholutecanet", followers: 0 },
    { id: "youtube", name: "YouTube", url: "https://youtube.com/@cholutecanet", followers: 0 },
    { id: "x", name: "X (Twitter)", url: "https://x.com/cholutecanet", followers: 0 },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("session_token");

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/social-networks`);
      if (response.data.networks && response.data.networks.length > 0) {
        setNetworks(response.data.networks);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (id, field, value) => {
    setNetworks(networks.map(n => 
      n.id === id ? { ...n, [field]: field === "followers" ? parseInt(value) || 0 : value } : n
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/config/social-networks`,
        { networks },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      toast.success("Redes sociales actualizadas");
    } catch (error) {
      console.error("Error saving:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Sesión expirada. Redirigiendo al login...");
        localStorage.removeItem("session_token");
        setTimeout(() => {
          window.location.href = '/admin/login';
        }, 1500);
      } else {
        toast.error(error.response?.data?.detail || "Error al guardar");
      }
    } finally {
      setSaving(false);
    }
  };

  const totalFollowers = networks.reduce((sum, n) => sum + (n.followers || 0), 0);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-redes-sociales">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Redes Sociales
            </h1>
            <p className="text-brand-textSecondary mt-1">
              Configura los seguidores de cada red social
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="rounded-full">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Guardar Cambios
          </Button>
        </div>

        {/* Total */}
        <Card className="bg-gradient-to-br from-primary to-primary/80">
          <CardContent className="py-6">
            <div className="text-center text-white">
              <p className="text-sm opacity-80 mb-1">Total de Seguidores</p>
              <p className="text-4xl font-bold">{totalFollowers.toLocaleString()}</p>
              <p className="text-sm opacity-80 mt-1">en todas las redes</p>
            </div>
          </CardContent>
        </Card>

        {/* Networks */}
        <div className="grid gap-4">
          {networks.map((network) => {
            const IconComponent = socialIcons[network.id] || Twitter;
            const colorClass = socialColors[network.id] || "bg-gray-500 text-white";
            
            return (
              <Card key={network.id}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClass}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>

                    {/* Name & URL */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Red Social</Label>
                        <p className="font-medium text-brand-text">{network.name}</p>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-gray-500">URL del Perfil</Label>
                        <Input
                          value={network.url}
                          onChange={(e) => handleChange(network.id, "url", e.target.value)}
                          placeholder="https://..."
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs text-gray-500">Seguidores</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            value={network.followers}
                            onChange={(e) => handleChange(network.id, "followers", e.target.value)}
                            placeholder="0"
                            className="font-mono"
                          />
                          <span className="text-sm text-gray-500 whitespace-nowrap">
                            = {formatNumber(network.followers)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Vista Previa</CardTitle>
            <CardDescription>Así se verá en el sitio público</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-900 rounded-xl p-6">
              <p className="text-white text-center mb-4">Síguenos en Redes Sociales</p>
              <div className="grid grid-cols-5 gap-3">
                {networks.map((network) => {
                  const IconComponent = socialIcons[network.id] || Twitter;
                  const colorClass = socialColors[network.id] || "bg-gray-500";
                  
                  return (
                    <div
                      key={network.id}
                      className={`${colorClass} rounded-xl p-3 text-center text-white`}
                    >
                      <IconComponent className="h-5 w-5 mx-auto mb-1" />
                      <p className="text-xs opacity-80">{network.name}</p>
                      <p className="font-bold">{formatNumber(network.followers)}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-gray-400 text-center text-sm mt-4">
                Más de <span className="text-yellow-400 font-bold">{totalFollowers.toLocaleString()}</span> personas nos siguen
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
